import { Box, Button, Icon, Typography } from "@outschool/backpack";
import { faFile, faFileImage, faFilePdf, faTrashAlt } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import React from "react";

import ExternalLink from "../ExternalLink";
import { Attachment, OnDeleteHandler } from "./types";

/*

A business-logic aware component which renders an Attachment as a block element
with file name + icon

 */
export interface AttachmentLineProps {
  attachment: Attachment; // the attachment to display
  onDelete: OnDeleteHandler; // if truthy, show a delete Button and use this as its onClick
}
export default function AttachmentLine({
  attachment,
  onDelete,
}: AttachmentLineProps) {
  const { t } = useTranslation(
    "ui-components-shared\\components\\attachments\\AttachmentLine",
  );
  const { file, fileUrl } = attachment;
  const fileSize =
    file.size > 1024 * 1024
      ? `${Math.round(file.size / 1024 / 1024)} MB`
      : `${Math.round(file.size / 1024)} KB`;

  return (
    <Box
      flex
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "neutral.50",
        padding: 12,
        borderRadius: 8,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <ExternalLink
          url={fileUrl}
          title={file.filename}
          sx={{
            display: "block",
            flexGrow: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          trackingName="attachment_line"
          trackingSharedProperties={{
            attachment_uid: attachment?.uid,
            file_mimetype: attachment?.file?.mimetype,
            file_url: fileUrl,
          }}
        >
          <Icon icon={getIconForMimeType(file.mimetype)} /> {file.filename}
        </ExternalLink>{" "}
        <Typography
          variant="inherit"
          sx={{
            color: "grey.300",
            flexShrink: 0,
            marginLeft: 4,
            whiteSpace: "nowrap",
          }}
        >
          ({fileSize})
        </Typography>
      </Box>
      {onDelete && (
        <React.Fragment>
          {" "}
          <Button
            variant="text"
            color="primary"
            onClick={(evt: any) => onDelete && onDelete(attachment, evt)}
            title={t("Delete Attachment")}
            startIcon={<Icon icon={faTrashAlt} />}
          >
            {t("Delete")}
          </Button>
        </React.Fragment>
      )}
    </Box>
  );
}

// TODO: should these be broken into a util file?

function getIconForMimeType(mimetype: string | null | undefined) {
  switch (mimetype) {
    case "image/jpeg":
    case "image/png":
      return faFileImage;
    case "application/pdf":
      return faFilePdf;
    default:
      return faFile;
  }
}
